import { useEffect, useMemo, useState } from 'react';
import { Plus } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { ConfirmDialog } from '@/components/feedback/ConfirmDialog';
import { FillPage } from '@/components/layout/PageContainer';
import { PageHeader } from '@/components/feedback/PageHeader';
import { useAuthStore } from '@/lib/auth/store';
import { USER_ROLES } from '@/lib/constants/roles';
import { AddPromptSheet } from '../components/AddPromptSheet';
import { downloadPromptAudio } from '../download';
import { Pagination } from '../components/Pagination';
import { PromptFilterBar } from '../components/PromptFilterBar';
import { PromptList } from '../components/PromptList';
import { TranscribeDialog } from '../components/TranscribeDialog';
import {
  useBulkDeletePrompts,
  useDeletePrompt,
  usePromptList,
} from '../api';
import { sortPromptsNewestFirst } from '../sort';
import type { PromptCategory, PromptRow } from '../types';

export function VoiceFilesPage() {
  const userId = useAuthStore((s) => s.userId);
  const role = useAuthStore((s) => s.role);
  // Only end-users can upload / synthesize prompts. Reseller (ADMIN),
  // super admin and sub-super-admin can browse and approve, not author.
  const canUpload = role === USER_ROLES.USER;
  // Reseller views their child users' prompts in read-only mode — they
  // shouldn't be deleting other users' content from this page.
  const canDelete = role !== USER_ROLES.ADMIN;
  const [category, setCategory] = useState<'all' | PromptCategory>('all');
  const [query, setQuery] = useState('');

  // The server endpoint's `remarks` is a free-text filter — not a category
  // filter — so we always fetch everything and filter client-side.
  const list = usePromptList();

  const filtered = useMemo<PromptRow[]>(() => {
    const rows = list.data ?? [];
    const q = query.trim().toLowerCase();

    // Hide soft-deleted (status 9) and still-pending (status 0) prompts —
    // matches legacy semantics for the "regular" voice-files view. Without
    // this filter, the row stays on screen after a delete (the GET endpoint
    // returns soft-deleted rows), making it look like delete didn't work.
    const byStatus = rows.filter((p) => {
      const n = Number(p.promptStatus);
      return n !== 9 && n !== 0;
    });
    const byCategory = byStatus.filter((p) =>
      category === 'all' ? true : p.promptCategory === category,
    );
    const bySearch = !q
      ? byCategory
      : byCategory.filter(
          (p) =>
            (p.fileName ?? '').toLowerCase().includes(q) ||
            (p.promptName ?? '').toLowerCase().includes(q) ||
            (p.remarks ?? '').toLowerCase().includes(q) ||
            (p.username ?? '').toLowerCase().includes(q),
        );
    // Customer-requested default: most recent first.
    return sortPromptsNewestFirst(bySearch);
  }, [list.data, query, category]);

  /* ---- Pagination ---- */
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  // Reset to first page whenever the active filter changes — otherwise
  // the user can land on a now-empty page after narrowing the result set.
  useEffect(() => {
    setPage(1);
  }, [query, category, pageSize]);

  const paged = useMemo(
    () => filtered.slice((page - 1) * pageSize, page * pageSize),
    [filtered, page, pageSize],
  );

  /* ---- Multi-select ---- */
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const toggleSelect = (id: number, next: boolean) =>
    setSelected((prev) => {
      const out = new Set(prev);
      if (next) out.add(id);
      else out.delete(id);
      return out;
    });
  const clearSelection = () => setSelected(new Set());

  /* ---- Delete ---- */
  const del = useDeletePrompt();
  const bulkDel = useBulkDeletePrompts();
  const [pendingDelete, setPendingDelete] = useState<PromptRow | null>(null);
  const [bulkConfirm, setBulkConfirm] = useState(false);
  const [transcribeTarget, setTranscribeTarget] = useState<PromptRow | null>(
    null,
  );

  /* ---- Add sheet ---- */
  const [addOpen, setAddOpen] = useState(false);

  return (
    <FillPage>
      <PageHeader
        title="Recordings"
        description="Audio prompts available to your campaigns. Upload files or synthesize from text."
        actions={
          canUpload ? (
            <Button onClick={() => setAddOpen(true)}>
              <Plus className="h-4 w-4" />
              Add Voice File
            </Button>
          ) : null
        }
      />

      {/* Filter bar pinned at top; list scrolls inside the remaining flex
          space — FillPage is a fixed viewport, so the list needs its own
          overflow region. */}
      <div className="flex min-h-0 flex-1 flex-col gap-3">
        <PromptFilterBar
          query={query}
          onQueryChange={setQuery}
          category={category}
          onCategoryChange={setCategory}
          total={filtered.length}
          selectedCount={canDelete ? selected.size : 0}
          onClearSelection={clearSelection}
          onBulkDelete={canDelete ? () => setBulkConfirm(true) : undefined}
          bulkBusy={bulkDel.isPending}
        />

        <div className="min-h-0 flex-1 overflow-y-auto pr-1">
          <PromptList
            prompts={paged}
            isLoading={list.isLoading}
            selectedIds={canDelete ? selected : undefined}
            onSelectChange={canDelete ? toggleSelect : undefined}
            onSelectAll={
              canDelete
                ? (next) => {
                    if (next) setSelected(new Set(paged.map((p) => p.promptId)));
                    else clearSelection();
                  }
                : undefined
            }
            onDelete={canDelete ? (p) => setPendingDelete(p) : undefined}
            onCopyUrl={(p) => {
              if (!p.promptUrl) return;
              void navigator.clipboard.writeText(p.promptUrl);
              toast.success('Audio URL copied');
            }}
            onDownload={(p) => {
              if (!p.promptUrl) return;
              downloadPromptAudio(p.promptUrl, p.fileName);
            }}
            onTranscribe={(p) => setTranscribeTarget(p)}
            emptyTitle={
              query
                ? 'No matching prompts'
                : category !== 'all'
                  ? `No ${category} prompts`
                  : 'No prompts yet'
            }
            emptyDescription={
              query
                ? 'Try clearing the search or picking another category.'
                : canUpload
                  ? 'Click "Add Voice File" to upload audio or synthesize from text.'
                  : 'No prompts have been uploaded yet.'
            }
          />
        </div>

        {filtered.length > 0 && (
          <Pagination
            page={page}
            pageSize={pageSize}
            total={filtered.length}
            onPageChange={setPage}
            onPageSizeChange={setPageSize}
          />
        )}
      </div>

      <AddPromptSheet open={addOpen} onOpenChange={setAddOpen} />

      <TranscribeDialog
        prompt={transcribeTarget}
        onOpenChange={(o) => !o && setTranscribeTarget(null)}
      />

      <ConfirmDialog
        open={pendingDelete !== null}
        onOpenChange={(o) => !o && setPendingDelete(null)}
        title="Delete prompt?"
        description={
          <>
            Prompt{' '}
            <span className="font-medium text-foreground">
              {pendingDelete?.fileName}
            </span>{' '}
            will be deleted. Campaigns referencing it will stop playing this
            file.
          </>
        }
        confirmLabel="Delete"
        tone="destructive"
        loading={del.isPending}
        onConfirm={() => {
          if (!pendingDelete) return;
          del.mutate(pendingDelete.promptId, {
            onSuccess: (res) => {
              toast.success(res?.message ?? `Deleted ${pendingDelete.fileName}`);
              setPendingDelete(null);
            },
            onError: () => toast.error('Could not delete the prompt.'),
          });
        }}
      />

      <ConfirmDialog
        open={bulkConfirm}
        onOpenChange={setBulkConfirm}
        title="Delete selected prompts?"
        description={
          <>
            {selected.size} prompt{selected.size === 1 ? '' : 's'} will be
            permanently removed.
          </>
        }
        confirmLabel="Delete all"
        tone="destructive"
        loading={bulkDel.isPending}
        onConfirm={() => {
          bulkDel.mutate(
            {
              userId: Number(userId),
              promptIds: Array.from(selected).join(','),
            },
            {
              onSuccess: (res) => {
                toast.success(
                  res?.message ?? `Deleted ${selected.size} prompts`,
                );
                clearSelection();
                setBulkConfirm(false);
              },
              onError: () => toast.error('Could not delete the prompts.'),
            },
          );
        }}
      />
    </FillPage>
  );
}
